import { DOCUMENT, effect, inject, Injectable, REQUEST, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs';

import { LandingSeo } from '@core/cms/landing.cms';
import { DEFAULT_LANGUAGE, isSupportedLanguage, SUPPORTED_LANGUAGES } from '@core/i18n/language';
import { LanguageService } from '@core/i18n/language.service';
import { environment } from '@env/environment';
import { cmsSeoToPageSeo } from '@core/seo/cms-seo.adapter';
import { PageSeo, PageSeoSocial, ResolvedPageSeo, SocialNetwork } from '@core/seo/seo.model';

/** `og:locale` value per supported language; falls back to the bare language code. */
const OG_LOCALES: Readonly<Record<string, string>> = {
    en: 'en_US',
    de: 'de_DE',
};

/** `id` of the JSON-LD `<script>` this service owns, so it can be replaced/removed idempotently. */
const STRUCTURED_DATA_ID: string = 'seo-structured-data';

/** Brand name used for `og:site_name` and the default `Organization`/`WebSite` structured data. */
const SITE_NAME: string = 'Tyme Group';

/** Marker attribute on the `<link rel="alternate" hreflang>` tags this service owns. */
const HREFLANG_MARKER: string = 'data-seo-hreflang';

/** i18n keys for the app-wide default title/description used when the CMS provides none. */
const DEFAULT_TITLE_KEY: string = 'seo.default.title';
const DEFAULT_DESCRIPTION_KEY: string = 'seo.default.description';

/** App-relative default share image, resolved to an absolute URL at apply time. */
const DEFAULT_IMAGE_PATH: string = '/assets/logo-tyme-group.svg';

/**
 * Applies page SEO — title, description/keywords/robots, canonical link, Open Graph + Twitter card
 * tags and JSON-LD — from a source-agnostic {@link PageSeo}. CMS payloads ({@link LandingSeo}) are
 * the common case (`applyCmsSeo`), but `setSeo` accepts any hand-authored data so future non-CMS
 * pages are covered.
 *
 * SSR-safe: it relies only on `@angular/platform-browser`'s `Meta`/`Title`, the injected `DOCUMENT`
 * and the SSR `REQUEST`, all of which work under Angular Universal, so tags are present in the
 * server-rendered HTML (what crawlers read) — not just patched in on the client.
 *
 * Canonical/`og:url` default to the **current absolute URL** when the CMS supplies no `canonicalURL`,
 * and the service tracks router navigations to keep that default in sync as the route changes.
 */
@Injectable({ providedIn: 'root' })
export class SeoService {
    private readonly _document: Document = inject(DOCUMENT);
    private readonly _meta: Meta = inject(Meta);
    private readonly _title: Title = inject(Title);
    private readonly _language: LanguageService = inject(LanguageService);
    private readonly _translate: TranslateService = inject(TranslateService);
    private readonly _router: Router = inject(Router);
    /** The SSR request (its URL carries the host/origin); `null` in the browser. */
    private readonly _request: Request | null = inject(REQUEST, { optional: true });

    private readonly _cmsUrl: string = environment.cmsUrl;
    /** Title rendered into `index.html`; restored by {@link reset}. */
    private readonly _defaultTitle: string = this._title.getTitle();

    /** Whether the active canonical came from the CMS; if so, navigation must not overwrite it. */
    private _canonicalFromCms: boolean = false;
    /** Previous route path (no query/fragment), to detect real page changes vs. param-only ones. */
    private _previousPath: string | null = null;

    /** Active route URL (path + query + fragment); updates on every `NavigationEnd`. */
    private readonly _navigationUrl: Signal<string> = toSignal(
        this._router.events.pipe(
            filter((event: unknown): event is NavigationEnd => event instanceof NavigationEnd),
            map((): string => this._router.url),
        ),
        { initialValue: this._router.url },
    );

    constructor() {
        // Keep the *default* canonical/og:url tracking the current route. A page navigation (path
        // change) drops any CMS canonical the previous page set; a param-only change keeps it.
        effect((): void => {
            const url: string = this._navigationUrl();
            const path: string = url.split(/[?#]/)[0];

            if (this._previousPath !== null && path !== this._previousPath) {
                this._canonicalFromCms = false;
            }

            this._previousPath = path;

            // hreflang is purely route-derived (never CMS-owned), so refresh it on every navigation.
            this._applyHreflang(path);

            if (!this._canonicalFromCms) {
                const absolute: string = this._absoluteUrl(url);

                this._applyCanonical(absolute);
                this._setProperty('og:url', absolute);
            }
        });
    }

    /** Maps the CMS `shared.seo` component into a {@link PageSeo} and applies it. */
    public applyCmsSeo(seo: LandingSeo): void {
        this.setSeo(cmsSeoToPageSeo(seo, this._cmsUrl));
    }

    /** Applies only the app-wide defaults (i18n title/description + asset logo). */
    public applyDefaultSeo(): void {
        this.setSeo({});
    }

    /**
     * Applies a {@link PageSeo}, filling any absent field from the app-wide defaults (i18n
     * title/description, asset logo). Pass `{}` for the pure default; pass CMS data and only its
     * gaps are defaulted.
     */
    public setSeo(seo: PageSeo): void {
        const title: string | null = seo.title ?? this._defaultTitleText();
        const description: string | null = seo.description ?? this._defaultDescriptionText();
        const imageUrl: string | null = seo.imageUrl ?? this._defaultImageUrl();
        // CMS canonical wins; otherwise default to the current absolute URL (kept in sync by the
        // navigation effect). The flag tells that effect whether it may overwrite the canonical.
        this._canonicalFromCms = !!seo.canonicalUrl;
        const canonicalUrl: string = seo.canonicalUrl ?? this._absoluteUrl(this._router.url);

        this._title.setTitle(title ?? this._defaultTitle);

        this._updateOrRemove('description', description);
        this._updateOrRemove('keywords', seo.keywords);
        this._updateOrRemove('robots', seo.robots);

        if (seo.viewport) {
            this._meta.updateTag({ name: 'viewport', content: seo.viewport });
        }

        this._applyCanonical(canonicalUrl);
        this._updateOpenGraph(seo, { title, description, imageUrl, canonicalUrl });
        this._updateStructuredData(seo.structuredData ?? this._defaultStructuredData());
    }

    /** Clears everything this service added and restores the default title. */
    public reset(): void {
        this._canonicalFromCms = false;
        this._title.setTitle(this._defaultTitle);

        for (const name of ['description', 'keywords', 'robots']) {
            this._meta.removeTag(`name="${name}"`);
        }

        for (const e of this._meta.getTags('property^="og:"')) {
            this._meta.removeTagElement(e);
        }

        for (const e of this._meta.getTags('name^="twitter:"')) {
            this._meta.removeTagElement(e);
        }

        this._removeCanonical();
        this._removeHreflang();
        this._removeStructuredData();
    }

    /** Updates a `name=…` meta tag, or removes it when the value is empty. */
    private _updateOrRemove(name: string, content: string | null | undefined): void {
        if (content) {
            this._meta.updateTag({ name, content });

            return;
        }

        this._meta.removeTag(`name="${name}"`);
    }

    /**
     * Emits Open Graph + Twitter tags. `resolved` carries the page-level title/description/image
     * already defaulted by {@link setSeo}; per-network `social` overrides win over those.
     */
    private _updateOpenGraph(seo: PageSeo, resolved: ResolvedPageSeo): void {
        const locale: string = OG_LOCALES[this._language.lang()] ?? this._language.lang();
        const facebook: PageSeoSocial | undefined = seo.social?.find(
            (s: PageSeoSocial): boolean => s.network === SocialNetwork.Facebook,
        );
        const twitter: PageSeoSocial | undefined = seo.social?.find(
            (s: PageSeoSocial): boolean => s.network === SocialNetwork.Twitter,
        );

        this._setProperty('og:type', 'website');
        this._setProperty('og:site_name', SITE_NAME);
        this._setProperty('og:locale', locale);
        this._setProperty('og:url', resolved.canonicalUrl);
        this._setProperty('og:title', facebook?.title ?? resolved.title);
        this._setProperty('og:description', facebook?.description ?? resolved.description);
        this._setProperty('og:image', facebook?.imageUrl ?? resolved.imageUrl);

        const twitterImage: string | null = twitter?.imageUrl ?? resolved.imageUrl;

        this._setName('twitter:card', twitterImage ? 'summary_large_image' : 'summary');
        this._setName('twitter:title', twitter?.title ?? resolved.title);
        this._setName('twitter:description', twitter?.description ?? resolved.description);
        this._setName('twitter:image', twitterImage);
    }

    private _defaultTitleText(): string | null {
        const value: string = this._translate.instant(DEFAULT_TITLE_KEY);

        return value && value !== DEFAULT_TITLE_KEY ? value : null;
    }

    private _defaultDescriptionText(): string | null {
        const value: string = this._translate.instant(DEFAULT_DESCRIPTION_KEY);

        return value && value !== DEFAULT_DESCRIPTION_KEY ? value : null;
    }

    /** Absolute URL to the default share image; relative when no origin is available (SSR edge). */
    private _defaultImageUrl(): string {
        return this._absoluteUrl(DEFAULT_IMAGE_PATH);
    }

    /**
     * Resolves an app-relative path to an absolute URL. Origin comes from the SSR {@link _request}
     * on the server (so it honours the proxied host) and `document.location` in the browser; when
     * neither is available the path is returned unchanged.
     */
    private _absoluteUrl(path: string): string {
        const origin: string = this._origin();

        return origin ? `${origin}${path}` : path;
    }

    private _origin(): string {
        if (this._request?.url) {
            try {
                return new URL(this._request.url).origin;
            } catch {
                // Malformed request URL — fall back to the document location below.
            }
        }

        return this._document.location?.origin ?? '';
    }

    private _setProperty(property: string, content: string | null | undefined): void {
        if (content) {
            this._meta.updateTag({ property, content }, `property="${property}"`);

            return;
        }

        this._meta.removeTag(`property="${property}"`);
    }

    private _setName(name: string, content: string | null | undefined): void {
        if (content) {
            this._meta.updateTag({ name, content });

            return;
        }

        this._meta.removeTag(`name="${name}"`);
    }

    private _applyCanonical(url: string | null | undefined): void {
        if (!url) {
            this._removeCanonical();

            return;
        }

        const existing: HTMLLinkElement | null =
            this._document.querySelector("link[rel='canonical']");

        if (existing) {
            existing.setAttribute('href', url);

            return;
        }

        const link: HTMLLinkElement = this._document.createElement('link');

        link.setAttribute('rel', 'canonical');
        link.setAttribute('href', url);
        this._document.head.appendChild(link);
    }

    private _removeCanonical(): void {
        const existing: HTMLLinkElement | null =
            this._document.querySelector("link[rel='canonical']");

        existing?.remove();
    }

    /**
     * Emits `<link rel="alternate" hreflang>` for every supported language plus `x-default`, built by
     * swapping the language segment of the current route path. Route-derived and re-applied on every
     * navigation, so a page change keeps the alternates in sync. Idempotent — its own tags are
     * replaced each call.
     */
    private _applyHreflang(path: string): void {
        this._removeHreflang();

        const segments: ReadonlyArray<string> = path.split('/').filter(Boolean);
        const rest: ReadonlyArray<string> = isSupportedLanguage(segments[0])
            ? segments.slice(1)
            : segments;
        const suffix: string = rest.length ? `/${rest.join('/')}` : '';

        for (const lang of SUPPORTED_LANGUAGES) {
            this._appendAlternate(lang, this._absoluteUrl(`/${lang}${suffix}`));
        }

        this._appendAlternate('x-default', this._absoluteUrl(`/${DEFAULT_LANGUAGE}${suffix}`));
    }

    private _appendAlternate(hreflang: string, href: string): void {
        const link: HTMLLinkElement = this._document.createElement('link');

        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', hreflang);
        link.setAttribute('href', href);
        link.setAttribute(HREFLANG_MARKER, '');
        this._document.head.appendChild(link);
    }

    private _removeHreflang(): void {
        const existing: NodeListOf<HTMLLinkElement> = this._document.querySelectorAll(
            `link[rel='alternate'][${HREFLANG_MARKER}]`,
        );

        for (const link of Array.from(existing)) {
            link.remove();
        }
    }

    /**
     * App-wide default JSON-LD (an `Organization` + `WebSite` graph) applied when the CMS supplies no
     * `structuredData`, so every page carries baseline structured data. Returns `null` when no origin
     * is resolvable (an SSR edge), leaving no script rather than emitting relative `@id`s.
     */
    private _defaultStructuredData(): unknown {
        const origin: string = this._origin();

        if (!origin) {
            return null;
        }

        return {
            '@context': 'https://schema.org',
            '@graph': [
                { '@type': 'Organization', name: SITE_NAME, url: origin },
                { '@type': 'WebSite', name: SITE_NAME, url: origin },
            ],
        };
    }

    private _updateStructuredData(data: unknown): void {
        this._removeStructuredData();

        if (!data) {
            return;
        }

        const script: HTMLScriptElement = this._document.createElement('script');

        script.id = STRUCTURED_DATA_ID;
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(data);
        this._document.head.appendChild(script);
    }

    private _removeStructuredData(): void {
        this._document.getElementById(STRUCTURED_DATA_ID)?.remove();
    }
}
